import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE, getImageUrl } from '../../utils/api'
import { toast } from 'react-toastify'

interface Highlight {
    _id: string;
    name: string;
    icon: string;
    description?: string;
}

export default function AdminLocalityHighlights() {
    const { token } = useAuth()
    const [highlights, setHighlights] = useState<Highlight[]>([])
    const [loading, setLoading] = useState(true)
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [icon, setIcon] = useState('fa-star')
    const [image, setImage] = useState<File | null>(null)
    const [preview, setPreview] = useState('')
    const [useImage, setUseImage] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [editId, setEditId] = useState<string | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)

    useEffect(() => {
        fetchHighlights()
    }, [])

    const fetchHighlights = async () => {
        try {
            const res = await fetch(`${API_BASE}/locality-highlights`)
            const data = await res.json()
            if (Array.isArray(data)) setHighlights(data)
        } catch (err) {
            console.error(err)
            toast.error('Failed to load highlights')
        } finally {
            setLoading(false)
        }
    }

    const handleImageUpload = async (file: File) => {
        const fd = new FormData()
        fd.append('image', file)
        setUploading(true)
        try {
            const res = await fetch(`${API_BASE}/upload/nearby-image`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: fd
            })
            const data = await res.json()
            if (data.url) {
                setPreview(data.url)
                return data.url
            }
            throw new Error('Upload failed')
        } catch (err) {
            toast.error('Image upload failed')
            return null
        } finally {
            setUploading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!name.trim() || !token) return
        
        setSubmitting(true)
        try {
            let finalIcon = useImage ? preview : icon
            
            const url = editId ? `${API_BASE}/locality-highlights/${editId}` : `${API_BASE}/locality-highlights`
            const method = editId ? 'PUT' : 'POST'

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ name: name.trim(), icon: finalIcon, description: description.trim() })
            })

            if (res.ok) {
                toast.success(editId ? 'Highlight synchronized.' : 'New highlight localized.')
                setIsModalOpen(false)
                resetForm()
                fetchHighlights()
            } else {
                const data = await res.json()
                toast.error(data.message || 'Operation failed')
            }
        } catch (err) {
            toast.error('Network error detected')
        } finally {
            setSubmitting(false)
        }
    }

    const resetForm = () => {
        setName('')
        setDescription('')
        setIcon('fa-star')
        setImage(null)
        setPreview('')
        setUseImage(false)
        setEditId(null)
    }

    const handleDelete = async (id: string) => {
        if (!token || !window.confirm('Are you sure you want to purge this highlight?')) return
        try {
            const res = await fetch(`${API_BASE}/locality-highlights/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            })
            if (res.ok) {
                toast.success('Highlight purged.')
                fetchHighlights()
            }
        } catch (err) {
            toast.error('Purge failed')
        }
    }

    const handleEdit = (h: Highlight) => {
        setName(h.name)
        setDescription(h.description || '')
        const isImg = !!(h.icon && (h.icon.includes('/') || !h.icon.startsWith('fa-')))
        setUseImage(isImg)
        if (isImg) {
            setPreview(h.icon)
            setIcon('')
        } else {
            setIcon(h.icon)
            setPreview('')
        }
        setEditId(h._id)
        setIsModalOpen(true)
    }

    return (
        <AdminLayout>
            <div className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-black mb-2 leading-none">Locality Highlights Registry</h1>
                    <p className="text-[14px] md:text-[15px] font-bold text-black leading-relaxed">Global definitions for premium sector attributes and visual nodes.</p>
                </div>
                <button 
                    onClick={() => { resetForm(); setIsModalOpen(true); }}
                    className="flex items-center gap-2 px-8 py-4 bg-white border-2 border-[#05144b] text-[#05144b] rounded-md font-bold transition-all shadow-md active:scale-95"
                >
                    <i className="fa-solid fa-plus-circle"></i>
                    <span>Inject New Highlight</span>
                </button>
            </div>

            <div className="bg-white rounded-md border border-blue-900/10 shadow-sm overflow-hidden min-h-[500px] animate-in fade-in zoom-in-95 duration-500">
                <div className="p-8 border-b border-blue-900/10 flex items-center justify-between bg-white/50">
                    <div>
                        <h2 className="text-xl font-bold text-black leading-none">Active Attribute Matrix</h2>
                        <p className="text-[12px] font-bold text-black/60 mt-1">{highlights.length} nodes registered in system</p>
                    </div>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-black">
                        <div className="animate-spin w-8 h-8 border-4 border-[#05144b] border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="text-sm font-bold">Scanning frequency registry...</p>
                    </div>
                ) : highlights.length === 0 ? (
                    <div className="py-32 text-center">
                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-gray-100 shadow-inner">
                            <i className="fa-solid fa-microchip text-black/10 text-3xl"></i>
                        </div>
                        <p className="text-black font-bold uppercase tracking-widest text-sm">No highlights detected</p>
                        <p className="text-xs text-black/40 font-bold mt-2">Standardize your sector metrics today.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr>
                                    <th className="px-8 py-5 text-left text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Iconography</th>
                                    <th className="px-8 py-5 text-left text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Descriptor</th>
                                    <th className="px-8 py-5 text-right text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Operations</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-900/5">
                                {highlights.map(h => (
                                    <tr key={h._id} className="group hover:bg-blue-50/30 transition-all">
                                        <td className="px-8 py-4">
                                            <div className="w-12 h-12 rounded-lg bg-white border border-gray-100 flex items-center justify-center text-black shadow-sm group-hover:scale-110 transition-transform overflow-hidden p-1">
                                                {h.icon && (h.icon.startsWith('fa-') || !h.icon.includes('/')) ? (
                                                    <i className={`fa-solid ${h.icon || 'fa-star'} text-xl`}></i>
                                                ) : h.icon ? (
                                                    <img src={getImageUrl(h.icon)} alt={h.name} className="w-full h-full object-contain" />
                                                ) : (
                                                    <i className="fa-solid fa-star text-xl opacity-20"></i>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-8 py-4">
                                            <span className="text-[15px] md:text-[16px] font-bold text-black leading-tight">{h.name}</span>
                                        </td>
                                        <td className="px-8 py-4 text-right">
                                            <div className="flex justify-end gap-3">
                                                <button 
                                                    className="w-10 h-10 rounded-md bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 transition-all flex items-center justify-center shadow-sm active:scale-90"
                                                    onClick={() => handleEdit(h)}
                                                    title="Modify Node"
                                                >
                                                    <i className="fa-solid fa-pen-nib text-xs"></i>
                                                </button>
                                                <button 
                                                    className="w-10 h-10 rounded-md bg-white border border-gray-100 text-rose-600 hover:bg-rose-50 transition-all flex items-center justify-center shadow-sm active:scale-90"
                                                    onClick={() => handleDelete(h._id)}
                                                    title="Purge Node"
                                                >
                                                    <i className="fa-solid fa-trash-can text-xs"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Premium Injection Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-[#05144b]/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white w-full max-w-2xl rounded-xl border border-gray-100 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-500">
                        <div className="px-8 py-6 border-b border-gray-50 flex items-center justify-between bg-white text-black">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-[#05144b] rounded-lg flex items-center justify-center text-white shadow-lg">
                                    <i className="fa-solid fa-microchip text-lg"></i>
                                </div>
                                <div>
                                    <h5 className="text-lg font-bold tracking-tight uppercase leading-none">{editId ? 'Modify Attribute' : 'Injection Protocol'}</h5>
                                    <p className="text-[9px] font-bold text-black/40 uppercase tracking-widest mt-1.5">Global metadata system</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setIsModalOpen(false)} 
                                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-all text-black"
                            >
                                <i className="fa-solid fa-xmark text-lg"></i>
                            </button>
                        </div>

                        <div className="p-8">
                            <form onSubmit={handleSubmit} className="space-y-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {/* Left Column: Metadata */}
                                    <div className="space-y-6">
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-bold text-black uppercase tracking-widest ml-1">Nomenclature</label>
                                            <input 
                                                type="text" 
                                                className="block w-full px-5 py-4 bg-gray-50 border border-gray-100 focus:ring-2 focus:ring-[#05144b] rounded-lg text-sm font-bold text-black transition-all outline-none shadow-inner placeholder:text-black/10"
                                                value={name} 
                                                onChange={e => setName(e.target.value)} 
                                                placeholder="Identification label (e.g. ROI Performance)" 
                                                required 
                                            />
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-bold text-black uppercase tracking-widest ml-1">Highlight Content / Description</label>
                                            <textarea 
                                                rows={6}
                                                className="block w-full px-5 py-4 bg-gray-50 border border-gray-100 focus:ring-2 focus:ring-[#05144b] rounded-lg text-sm font-bold text-black transition-all outline-none shadow-inner placeholder:text-black/10"
                                                value={description} 
                                                onChange={e => setDescription(e.target.value)} 
                                                placeholder="Prime SG Highway location with strong connectivity & high growth potential." 
                                            />
                                        </div>
                                    </div>

                                    {/* Right Column: Visual Identity */}
                                    <div className="space-y-6">
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-3 mb-1">
                                                <div className="w-1 h-3 bg-[#05144b] rounded-full"></div>
                                                <label className="text-[10px] font-bold text-black uppercase tracking-widest leading-none">Iconographic Identity</label>
                                            </div>

                                            <div className="flex items-center gap-3 bg-gray-50 p-1 rounded-xl border border-gray-100 shadow-inner">
                                                <button 
                                                    type="button"
                                                    onClick={() => setUseImage(false)}
                                                    className={`flex-1 py-3 text-[10px] font-bold uppercase tracking-widest rounded-lg transition-all ${!useImage ? 'bg-[#05144b] text-white shadow-lg' : 'text-black/40 hover:text-black'}`}
                                                >
                                                    Icon
                                                </button>
                                                <button 
                                                    type="button"
                                                    onClick={() => setUseImage(true)}
                                                    className={`flex-1 py-3 text-[10px] font-bold uppercase tracking-widest rounded-lg transition-all ${useImage ? 'bg-[#05144b] text-white shadow-lg' : 'text-black/40 hover:text-black'}`}
                                                >
                                                    Asset
                                                </button>
                                            </div>

                                            {!useImage ? (
                                                <div className="flex flex-col gap-4 animate-in slide-in-from-left-2 duration-300">
                                                    <div className="w-20 h-20 bg-white border border-gray-100 flex items-center justify-center text-black rounded-2xl shadow-xl text-4xl mx-auto">
                                                        <i className={`fa-solid ${icon || 'fa-star'}`}></i>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <input
                                                            type="text"
                                                            className="block w-full px-5 py-4 bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] rounded-xl text-sm font-bold text-black transition-all outline-none shadow-sm text-center"
                                                            value={icon}
                                                            onChange={e => setIcon(e.target.value)}
                                                            placeholder="fa-shield-halved"
                                                        />
                                                        <p className="text-[9px] text-black/40 font-bold uppercase tracking-widest text-center">Use FontAwesome 6 classes</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="animate-in slide-in-from-right-2 duration-300">
                                                    <div className={`relative h-44 rounded-2xl border-2 border-dashed transition-all flex flex-col items-center justify-center p-4 ${preview ? 'border-[#05144b] bg-gray-50' : 'border-gray-100 hover:border-[#05144b] bg-white shadow-sm'}`}>
                                                        {uploading ? (
                                                            <div className="text-center">
                                                                <div className="animate-spin h-6 w-6 border-2 border-[#05144b] border-t-transparent rounded-full mx-auto mb-2"></div>
                                                                <p className="text-[9px] font-bold text-black uppercase tracking-widest">Uploading Asset...</p>
                                                            </div>
                                                        ) : preview ? (
                                                            <div className="text-center">
                                                                <div className="w-20 h-20 mx-auto rounded-lg overflow-hidden bg-white p-1 shadow-md border border-gray-100 mb-2">
                                                                    <img src={getImageUrl(preview)} alt="Preview" className="w-full h-full object-contain" />
                                                                </div>
                                                                <p className="text-[9px] font-bold text-black uppercase tracking-widest">Replace Asset</p>
                                                            </div>
                                                        ) : (
                                                            <div className="text-center">
                                                                <i className="fa-solid fa-cloud-arrow-up text-black text-2xl mb-2 opacity-10"></i>
                                                                <p className="text-[10px] font-bold text-black uppercase tracking-widest">Transmit Image</p>
                                                            </div>
                                                        )}
                                                        <input 
                                                            type="file" 
                                                            className="absolute inset-0 opacity-0 cursor-pointer z-10"
                                                            onChange={async (e) => {
                                                                const file = e.target.files?.[0]
                                                                if (file) await handleImageUpload(file)
                                                            }} 
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-4 pt-4 border-t border-gray-100">
                                    <button 
                                        type="button" 
                                        onClick={() => setIsModalOpen(false)}
                                        className="flex-1 py-4 text-[10px] font-bold text-black/30 hover:text-rose-600 transition-colors uppercase tracking-widest"
                                    >
                                        Disengage
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting || (useImage && uploading)}
                                        className="flex-[2] py-4 bg-white border-2 border-[#05144b] text-[#05144b] text-[10px] font-bold uppercase tracking-[0.2em] rounded-lg shadow-md active:scale-95 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            <>
                                                <i className="fa-solid fa-spinner fa-spin"></i>
                                                Synchronizing...
                                            </>
                                        ) : (
                                            <>
                                                <i className={`fa-solid ${editId ? 'fa-pen-nib' : 'fa-check-double'} text-sm`}></i>
                                                {editId ? 'Authorize Update' : 'Authorize Injection'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}
